import AvatarGroup from '@atlaskit/avatar-group/avatar-group';

import { cssMap } from '@atlaskit/css';

import Heading from '@atlaskit/heading/heading';

import { ConfluenceIcon } from '@atlaskit/logo/confluence-icon';
import { JiraProductDiscoveryIcon } from '@atlaskit/logo/jira-product-discovery/icon';
import { LoomIcon } from '@atlaskit/logo/loom/icon';

import { Anchor } from '@atlaskit/primitives/compiled/anchor';

import { Box } from '@atlaskit/primitives/compiled/box';

import { Stack } from '@atlaskit/primitives/compiled/stack';

import { token } from '@atlaskit/tokens';

import { css } from '@compiled/react';

import { type Product } from '@atlassian/expansion-services/services';

import { type FunctionComponent, type ReactNode, type MouseEvent } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';

import type { Collaborator, TargetProduct } from '../types';

import {
	registeredContentByProduct,
	registeredContentConfluence,
	registeredContentJpd,
	registeredContentLoom,
} from './content';

const logoByVariant: Record<TargetProduct, ReactNode> = {
	loom: <LoomIcon appearance="brand" size="xxsmall" />,
	'jira-product-discovery': <JiraProductDiscoveryIcon appearance="brand" size="xxsmall" />,
	confluence: <ConfluenceIcon appearance="brand" size="xxsmall" />,
};

const facePileBorderColorByVariant: Record<TargetProduct, string> = {
	loom: token('color.border.brand'),
	confluence: token('color.border.brand'),
	'jira-product-discovery': token('color.border.discovery'),
};

const inlineLogoStyles = css({
	display: 'inline-flex',
	lineHeight: 0,
	transformOrigin: 'center',
	transitionDuration: token('motion.duration.short'),
	transitionProperty: 'transform',
	transitionTimingFunction: token('motion.easing.inout.bold'),
	verticalAlign: 'middle',
	'&:hover': {
		transform: 'rotate(-6deg) scale(1.1)',
	},
	'@media (prefers-reduced-motion: reduce)': {
		transitionProperty: 'none',
		'&:hover': {
			transform: 'none',
		},
	},
});

const styles = cssMap({
	facePile: {
		pointerEvents: 'none',
	},
	link: {
		textDecoration: 'none',
		'&:hover': {
			textDecoration: 'underline',
		},
	},
	inlineBox: {
		display: 'inline-block',
	},
});

const renderInlineLogo = (logo: ReactNode) => <span css={inlineLogoStyles}>{logo}</span>;

type BodyProps = {
	targetProduct: TargetProduct;
	collaborators: Collaborator[];
};

export const productNameByVariant: Record<TargetProduct, ReactNode> = {
	loom: <FormattedMessage {...registeredContentLoom.default.productName} />,
	'jira-product-discovery': <FormattedMessage {...registeredContentJpd.default.productName} />,
	confluence: <FormattedMessage {...registeredContentConfluence.default.productName} />,
};

const FacePile: FunctionComponent<BodyProps> = ({ targetProduct, collaborators }) => {
	const { formatMessage } = useIntl();

	if (collaborators.length === 0) {
		return null;
	}

	const content = registeredContentByProduct[targetProduct].default;
	const avatarBorderColor = facePileBorderColorByVariant[targetProduct];

	return (
		<Box xcss={styles.facePile}>
			<AvatarGroup
				appearance="stack"
				size="small"
				maxCount={2}
				data={collaborators.map(({ name, picture }) => ({
					name,
					src: picture,
					borderColor: avatarBorderColor,
				}))}
				overrides={{
					MoreIndicator: {
						render: (Component, props) => (
							<Component {...props} borderColor={token('elevation.surface.overlay')} />
						),
					},
				}}
				isTooltipDisabled
				onMoreClick={() => {}}
				label={formatMessage(content.facePileLabel)}
			/>
		</Box>
	);
};

const Description: FunctionComponent<BodyProps> = ({ targetProduct, collaborators }) => {
	const content = registeredContentByProduct[targetProduct].default;
	const productName = productNameByVariant[targetProduct];
	const logo = logoByVariant[targetProduct];
	const [firstCollaborator, ...rest] = collaborators;

	if (!firstCollaborator) {
		return (
			<Heading size="xsmall">
				<FormattedMessage
					{...content.descriptionTeam}
					values={{
						productName,
						Logo: () => renderInlineLogo(logo),
						InlineBlock: (chunks) => <Box xcss={styles.inlineBox}>{chunks}</Box>,
					}}
				/>
			</Heading>
		);
	}

	if (rest.length === 0) {
		return (
			<Heading size="xsmall">
				<FormattedMessage
					{...content.descriptionSingle}
					values={{
						firstCollaborator: firstCollaborator.name,
						productName,
						Logo: () => renderInlineLogo(logo),
						InlineBlock: (chunks) => <Box xcss={styles.inlineBox}>{chunks}</Box>,
					}}
				/>
			</Heading>
		);
	}

	return (
		<Heading size="xsmall">
			<FormattedMessage
				{...content.descriptionMultiple}
				values={{
					firstCollaborator: firstCollaborator.name,
					collaboratorCount: rest.length,
					productName,
					Logo: () => renderInlineLogo(logo),
					InlineBlock: (chunks) => <Box xcss={styles.inlineBox}>{chunks}</Box>,
				}}
			/>
		</Heading>
	);
};

type Props = BodyProps & {
	tenantId: string;
	userId: string;
	collaborators: Collaborator[];
	sourceProduct: Product;
	crossJoinUrl: string;
	messageTemplateId?: string;
	onRequestAccess: (event: MouseEvent<HTMLAnchorElement>) => Promise<void>;
};

export const Body: FunctionComponent<Props> = ({
	targetProduct,
	collaborators,
	crossJoinUrl,
	onRequestAccess,
}) => {
	const content = registeredContentByProduct[targetProduct].default;

	return (
		<Stack space="space.100">
			<FacePile targetProduct={targetProduct} collaborators={collaborators} />
			<Description targetProduct={targetProduct} collaborators={collaborators} />
			<Anchor href={crossJoinUrl} xcss={styles.link} onClick={onRequestAccess}>
				<FormattedMessage {...content.cta} />
			</Anchor>
		</Stack>
	);
};
